/*
  Upload helpers (wave 3 foundation) — used by <FileDropzone upload={…} /> and any page that sends files.

    import { uploadFile, formatFileSize, fileMatchesAccept } from '@/lib/upload';
    const doc = await uploadFile('/documents', file, {
      fields: { project_id: id, title },                 // extra form fields
      onProgress: (pct) => setPct(pct),                  // 0–100 (null when the browser cannot tell)
      signal: controller.signal,                         // cancel
    });

  Goes through the shared `api` client (token, 401 handling). Errors are axios errors — show them with
  getErrorMessage(err). The backend checks type and size again (upload_utils.save_upload); these checks are
  only so people hear about a wrong file before waiting for the upload.
*/
import api from '@/lib/api';

/** XHR upload progress events exist (not in some test doubles / very old browsers). */
function canReportProgress() {
  try {
    return typeof XMLHttpRequest !== 'undefined' && typeof new XMLHttpRequest().upload?.addEventListener === 'function';
  } catch {
    return false;
  }
}

export async function uploadFile(url, file, {
  field = 'file', fields = {}, onProgress, signal, method = 'post', filename,
} = {}) {
  const progress = Boolean(onProgress) && canReportProgress();
  const form = new FormData();
  Object.entries(fields || {}).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    form.append(key, value instanceof Blob ? value : String(value));
  });
  if (filename) form.append(field, file, filename);
  else form.append(field, file);
  if (onProgress && !progress) onProgress(null); // unknown progress: the bar shows the indeterminate state
  const response = await api.request({
    url,
    method,
    data: form,
    signal,
    headers: { 'Content-Type': 'multipart/form-data' },
    ...(progress ? {
      onUploadProgress: (event) => {
        const total = event.total || file?.size || 0;
        onProgress(total ? Math.min(100, Math.round((event.loaded / total) * 100)) : null);
      },
    } : {}),
  });
  if (onProgress) onProgress(100);
  return response.data;
}

/** 1536 → '1.5 KB', 2_400_000 → '2.3 MB'. */
export function formatFileSize(bytes) {
  const size = Number(bytes);
  if (!Number.isFinite(size) || size < 0) return '';
  if (size < 1024) return `${size} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = size / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value >= 10 ? Math.round(value) : Math.round(value * 10) / 10} ${units[unit]}`;
}

/** Does `file` match an <input accept> string ('.pdf,.dwg,image/*') or array? Empty accept = anything. */
export function fileMatchesAccept(file, accept) {
  const rules = (Array.isArray(accept) ? accept : String(accept || '').split(','))
    .map((rule) => rule.trim().toLowerCase())
    .filter(Boolean);
  if (!rules.length) return true;
  const name = String(file?.name || '').toLowerCase();
  const type = String(file?.type || '').toLowerCase();
  return rules.some((rule) => {
    if (rule.startsWith('.')) return name.endsWith(rule);
    if (rule.endsWith('/*')) return type.startsWith(rule.slice(0, -1));
    return type === rule;
  });
}

/** Short human list of an accept string: '.pdf,.dwg,image/*' → 'PDF, DWG, images'. */
export function describeAccept(accept) {
  const rules = (Array.isArray(accept) ? accept : String(accept || '').split(','))
    .map((rule) => rule.trim())
    .filter(Boolean);
  return rules
    .map((rule) => {
      if (rule.startsWith('.')) return rule.slice(1).toUpperCase();
      if (rule === 'image/*') return 'images';
      if (rule === 'video/*') return 'videos';
      if (rule === 'application/pdf') return 'PDF';
      return rule;
    })
    .filter((label, index, all) => all.indexOf(label) === index)
    .join(', ');
}
